// Per-step coach-mark ("step guide") content shown by the floating guide button.
// Extracted verbatim from the deployed build.

import { TOTAL_JOURNEY_MINUTES } from "../components/ProgressBar";

export interface CoachMark {
  kicker: string;
  title: string;
  desc: string;
  bullets: string[];
  sideTitle: string;
  sideLines: string[];
  chips: string[];
  actions: Array<{ label: string; href?: string; external?: boolean; action?: "print" }>;
}

export const coachMarks: Record<number, { ar: CoachMark; en: CoachMark }> =
{
  1: {
    ar: {
      kicker: "ابدأ من هنا",
      title: "سجّل دخولك بحساب العمل، هذه هي الخطوة الأولى.",
      desc: "حساب Microsoft 365 الذي ستسجّل به هو نفس الحساب المستخدم في Outlook وTeams.",
      bullets: [
        "استخدم حساب الشركة (ينتهي بـ @kaden-sa.com)، وليس حسابك الشخصي.",
      ],
      sideTitle: "معلومات سريعة",
      sideLines: [`المدة: حوالي ${TOTAL_JOURNEY_MINUTES} دقيقة للرحلة كاملة`, "الهدف: إعداد حساباتك والتعريف بالأنظمة", "لا حاجة لتحميل أي برنامج، كل شيء يتم عبر المتصفح"],
      chips: [],
      actions: [
        { label: "اضغط هنا للتواصل مع تقنية المعلومات", href: `mailto:it@kaden-sa.com` },
        { label: "اضغط هنا لفتح بوابة الدعم الفني", href: "https://it.kaden-sa.com", external: true },
      ],
    },
    en: {
      kicker: "Start here",
      title: "Sign in with your work account, this is the first step.",
      desc: "The Microsoft 365 account you sign in with is the same one you use for Outlook and Teams.",
      bullets: [
        "Use your company account (ending in @kaden-sa.com), not a personal email.",
      ],
      sideTitle: "Quick information",
      sideLines: [
        `Duration: approximately ${TOTAL_JOURNEY_MINUTES} minutes for the complete journey`,
        "Objective: set up your accounts and introduce you to the systems",
        "No software installation required, everything runs in your browser",
      ],
      chips: [],
      actions: [
        { label: "Click here to contact IT", href: `mailto:it@kaden-sa.com` },
        { label: "Click here to open the IT Support Portal", href: "https://it.kaden-sa.com", external: true },
      ],
    },
  },
  2: {
    ar: {
      kicker: "بياناتك من حساب الشركة",
      title: "هذه بياناتك كما وصلتنا من قسم الموارد البشرية وتقنية المعلومات.",
      desc: "البيانات تظهر للاطلاع فقط، لا حاجة لتأكيدها أو تعديلها. لو لاحظت خطأً ستتولّى الموارد البشرية تصحيحه في النظام الرسمي ومن ثم تنعكس البيانات تلقائياً عند تسجيل دخولك التالي.",
      bullets: ["البيانات تُسحب تلقائياً من أنظمة الشركة.", "لا يمكن التعديل من هذه الصفحة، هذه نسخة للاستعراض.", "في حال أي خطأ، تتم متابعته مع الموارد البشرية ولا يؤثر على إكمال الرحلة."],
      sideTitle: "بعد هذه الخطوة",
      sideLines: ["ستستعرض سياسات وإجراءات كادن", "بعدها أسئلة قسمك ثم أنظمة الشركة المناسبة لدورك"],
      chips: [],
      actions: [{ label: "اضغط هنا للإبلاغ عن خطأ في البيانات", href: `mailto:it@kaden-sa.com?subject=Onboarding%20profile%20data%20review` }],
    },
    en: {
      kicker: "Your details from the company account",
      title: "These are your details as shared with us by HR & IT.",
      desc: "The data is shown for your reference only, no confirmation or edits are needed here. If you spot an error, HR will correct it in the official system and the change will reflect automatically next time you sign in.",
      bullets: [
        "Information is fetched automatically from the company systems.",
        "No edits are possible on this page, it is read-only.",
        "Any data error is handled with HR and does not block your journey.",
      ],
      sideTitle: "After this step",
      sideLines: ["Review the Kaden policies & procedures", "Then your department FAQ and the systems suited to your role"],
      chips: [],
      actions: [{ label: "Click here to report a data discrepancy", href: `mailto:it@kaden-sa.com?subject=Onboarding%20profile%20data%20review` }],
    },
  },
  3: {
    ar: {
      kicker: "سياسات وإجراءات",
      title: "تعرّف على أهم سياسات وإجراءات كادن قبل مباشرة عملك.",
      desc: "افتح كل فصل من فصول الدليل واحداً تلواً الآخر، لن تنتقل للخطوة التالية إلا بعد الاطلاع عليها جميعاً.",
      bullets: ["يمكنك الانتقال بين الفصول من شريط التقدّم العلوي.", "لأي توضيح إضافي تواصل مع إدارة الموارد البشرية."],
      sideTitle: "ملاحظة",
      sideLines: ["الفصول قابلة للتحديث من الإدارة في أي وقت"],
      chips: [],
      actions: [{ label: "التواصل مع الموارد البشرية", href: "mailto:hr@kaden-sa.com?subject=Policies%20question" }],
    },
    en: {
      kicker: "Policies & procedures",
      title: "Get to know Kaden's key policies and procedures before starting your work.",
      desc: "Open each chapter of the guide one after another, you can't continue until you've reviewed all of them.",
      bullets: [
        "You can navigate between chapters from the progress bar at the top.",
        "For any additional clarification, contact the HR department.",
      ],
      sideTitle: "Note",
      sideLines: ["Chapters can be updated by admins at any time"],
      chips: [],
      actions: [{ label: "Contact HR", href: "mailto:hr@kaden-sa.com?subject=Policies%20question" }],
    },
  },
  4: {
    ar: {
      kicker: "أسئلة وإجابات قسمك",
      title: "تعرّف على أهم المعلومات والسياسات الخاصة بقسمك قبل الدخول إلى الأنظمة.",
      desc: "هذه قائمة بالأسئلة الأكثر شيوعاً التي يطرحها الموظفون الجدد في قسمك، مع الإجابات الموثّقة من إدارتك. اضغط على أي سؤال لعرض إجابته.",
      bullets: [
        "الإجابات يديرها الأدمن من لوحة الإدارة، يمكن إضافة وحذف الأسئلة في أي وقت.",
        "إذا لم تجد إجابة سؤالك هنا، يُرجى التواصل مع مديرك المباشر أو إدارة الموارد البشرية.",
        "يمكنك العودة لهذه الصفحة لاحقاً متى أردت من خلال إعادة فتح الرحلة.",
      ],
      sideTitle: "ملاحظة",
      sideLines: ["الأسئلة مخصّصة حسب القسم الذي اخترته", "الإجابات قابلة للتحديث بشكل مستمر", "لا تتطلب أي إجراء، مجرد قراءة"],
      chips: [],
      actions: [{ label: "التواصل مع الموارد البشرية", href: "mailto:hr@kaden-sa.com?subject=Onboarding%20FAQ%20question" }],
    },
    en: {
      kicker: "Department Q&A",
      title: "Get to know the key information and policies for your department before entering the systems.",
      desc: "This is a list of the most frequently asked questions by new employees in your department, with documented answers from your management. Click any question to view its answer.",
      bullets: [
        "Answers are managed by the admin from the dashboard, questions can be added or removed at any time.",
        "If you cannot find the answer to your question here, please contact your direct manager or HR.",
        "You can return to this page later anytime by reopening the journey.",
      ],
      sideTitle: "Note",
      sideLines: ["Questions are tailored to your selected department", "Answers are continuously updated", "No action required, just reading"],
      chips: [],
      actions: [{ label: "Contact HR", href: "mailto:hr@kaden-sa.com?subject=Onboarding%20FAQ%20question" }],
    },
  },
  5: {
    ar: {
      kicker: "الأنظمة",
      title: "هذه هي الأنظمة التي ستستخدمها في عملك، اطّلع على كل بطاقة واقرأ الوصف.",
      desc: "لا تتطلب جميع الأنظمة تسجيل دخول فوري، وبعضها يحتاج صلاحية خاصة يتولى تقنية المعلومات تفعيلها.",
      bullets: ["اطّلع على كل بطاقة حتى وإن لم تكن بحاجة إلى النظام حالياً."],
      sideTitle: "ملاحظة",
      sideLines: ["الأنظمة الأخرى غير المرتبطة بدورك معروضة في نهاية الصفحة للاطلاع"],
      chips: [],
      actions: [
        { label: "Kaden Gate", href: "https://kadensa.sharepoint.com/sites/KadenGate/", external: true },
        { label: "بوابة الدعم الفني", href: "https://it.kaden-sa.com", external: true },
      ],
    },
    en: {
      kicker: "Systems",
      title: "These are the systems you will use in your work, review each card and read the description.",
      desc: "Not all systems require immediate sign-in, and some need a special permission the IT department will activate for you.",
      bullets: ["Review each card even if you do not need the system at this time."],
      sideTitle: "Note",
      sideLines: ["Systems outside your path are shown at the bottom of the page for awareness"],
      chips: [],
      actions: [
        { label: "Kaden Gate", href: "https://kadensa.sharepoint.com/sites/KadenGate/", external: true },
        { label: "IT Support Portal", href: "https://it.kaden-sa.com", external: true },
      ],
    },
  },
  6: {
    ar: {
      kicker: "مراجعة القائمة",
      title: "قبل إنهاء الرحلة، تأكّد من أن جميع العناصر الأساسية قد اكتملت.",
      desc: "يمثل كل عنصر متطلباً يجب أن يكون جاهزاً قبل مباشرة العمل فعلياً.",
      bullets: ["إن لم يكتمل عنصر ما بعد، ضعه كـ«لم يكتمل بعد» بدلاً من التوقف، وسيصل تلقائياً لفريق تقنية المعلومات ومديرك المباشر للمتابعة."],
      sideTitle: "الهدف",
      sideLines: ["أن تكون جاهزاً تقنياً ليومك الأول"],
      chips: [],
      actions: [{ label: "فتح تذكرة دعم", href: "https://it.kaden-sa.com", external: true }],
    },
    en: {
      kicker: "Review checklist",
      title: "Before completing the journey, ensure that all essential items have been addressed.",
      desc: "Each item represents a requirement that should be fulfilled before you begin your work.",
      bullets: ["If an item isn't done yet, mark it \"Not completed yet\" instead of getting stuck, it will automatically reach IT and your manager for follow-up."],
      sideTitle: "Objective",
      sideLines: ["To be technically prepared for your first day"],
      chips: [],
      actions: [{ label: "Open a support ticket", href: "https://it.kaden-sa.com", external: true }],
    },
  },
  7: {
    ar: {
      kicker: "تهانينا!",
      title: "أتممت رحلة التأهيل، أنت جاهز لبدء العمل في كادن.",
      desc: 'حمّل ملخص رحلتك (PDF) وراجع قسم "الخطوات التالية" أدناه. عُد إلى بريدك الإلكتروني وKaden Gate لأي تعليمات إضافية.',
      bullets: ["راجع بريدك الإلكتروني، قد تكون وردت إليك دعوات إضافية."],
      sideTitle: "الخطوات القادمة",
      sideLines: ["راجع بريدك الإلكتروني وتحقّق من الدعوات", "ادخل إلى Kaden Gate واستكشف محتوياتها"],
      chips: [],
      actions: [{ label: "Kaden Gate", href: "https://kadensa.sharepoint.com/sites/KadenGate/", external: true }],
    },
    en: {
      kicker: "Congratulations!",
      title: "You have completed the onboarding journey, you are ready to begin working at Kaden.",
      desc: 'Download your journey summary (PDF) and review the "Next Steps" section below. Check your email and Kaden Gate for any additional instructions.',
      bullets: ["Check your email, you may have received additional invitations."],
      sideTitle: "Next steps",
      sideLines: ["Check your email for invitations", "Access Kaden Gate and explore its content"],
      chips: [],
      actions: [{ label: "Kaden Gate", href: "https://kadensa.sharepoint.com/sites/KadenGate/", external: true }],
    },
  },
};
;
